import type { TrackerCopy } from './types'

/** Contenido del tracker en italiano (mercado italiano). */
export const TRACKER_COPY_IT: TrackerCopy = {
  meta: {
    title: "Dov'è Babbo Natale? Mappa e conto alla rovescia di Natale",
    description:
      'Scopri quanto manca a Natale, dove si trova Babbo Natale e a che ora arriverà. Segui il suo viaggio su una mappa mondiale interattiva.',
    keywords: [
      'dove si trova babbo natale',
      'quanto manca a natale',
      'dov e babbo natale',
      'dove passa babbo natale',
      'a che ora arriva babbo natale',
      'viaggio di babbo natale',
      'conto alla rovescia natale',
      'quanti giorni mancano a natale',
      'santa tracker',
      'mappa babbo natale',
      'seguire babbo natale',
      'traccia babbo natale',
    ],
    schemaName: 'Mappa e conto alla rovescia di Babbo Natale',
  },

  hero: {
    badge: {
      preparing: 'Officina del Polo Nord',
      liftoff: 'Ultimi preparativi',
      traveling: 'Consegne in corso',
      completed: 'Missione compiuta',
    },
    h1: {
      preparing: 'Quanto manca all\'arrivo di Babbo Natale?',
      liftoff: 'Babbo Natale sta per decollare',
      traveling: "Dov'è Babbo Natale adesso?",
      completed: 'Babbo Natale è già tornato al Polo Nord',
    },
    intro: {
      preparing:
        'Al Polo Nord non ci si ferma: gli elfi impacchettano regali, le renne si allenano e la grande lista viene controllata ancora e ancora. Qui puoi vedere quanto manca a Natale e come avanzano i preparativi.',
      liftoff:
        'Mancano poche ore al viaggio più lungo dell\'anno. La slitta è caricata, Rudolph ha il naso acceso e al Polo Nord nessuno chiude occhio.',
      traveling:
        'È la Vigilia di Natale e la slitta sta girando il mondo. Segui il suo percorso sulla mappa, guarda da dove è appena passato e scopri quando passerà dalla tua zona.',
      completed:
        'Tutti i regali sono consegnati e le renne riposano al Polo Nord. Il prossimo Natale è già in preparazione: eccoti il conto alla rovescia.',
    },
    toMap: 'Vedi la mappa',
  },

  countdown: {
    heading: {
      preparing: 'Babbo Natale arriverà nella tua zona tra',
      liftoff: 'La slitta decolla tra',
      traveling: 'Arriva nella tua zona tra',
      completed: 'Al prossimo Natale mancano',
    },
    units: { days: 'giorni', hours: 'ore', minutes: 'minuti', seconds: 'secondi' },
    christmasNote: 'Il Natale è il 25 dicembre: mancano {days} giorni.',
    christmasToday: 'Oggi è Natale!',
    arrivingNow: 'Sta sorvolando la tua zona!',
    ariaLabel: 'Conto alla rovescia per l\'arrivo di Babbo Natale',
  },

  status: {
    preparing: {
      far: [
        'Gli elfi stanno già preparando i primi giocattoli dell\'anno.',
        'Babbo Natale ha aperto un quaderno nuovo per la grande lista.',
        'Nell\'officina si provano le idee regalo per il prossimo Natale.',
        'Le renne rientrano pian piano dalle loro vacanze.',
      ],
      months: [
        'L\'officina del Polo Nord lavora senza sosta.',
        'Babbo Natale sta ricontrollando la sua grande lista.',
        'Le renne hanno iniziato gli allenamenti.',
        'Arrivano le prime lettere da tutto il mondo.',
      ],
      weeks: [
        'La signora Claus prepara i biscotti per tutta l\'officina.',
        'Babbo Natale rilegge la lista per la seconda volta.',
        'Gli elfi impacchettano regali a tutta velocità.',
        'La slitta è in officina, pronta per la revisione.',
      ],
      days: [
        'Manca pochissimo al decollo della slitta!',
        'Gli elfi chiudono gli ultimi sacchi.',
        'Rudolph si sta esercitando per la grande notte.',
        'Al Polo Nord ormai nessuno dorme.',
      ],
      hours: [
        'Babbo Natale sta caricando gli ultimi regali.',
        'La slitta è pronta al decollo.',
        'Le renne sono già attaccate e aspettano il segnale.',
        'Rudolph ha già acceso il suo naso.',
      ],
    },
    liftoff: [
      'Babbo Natale sta caricando gli ultimi regali.',
      'Rudolph ha già acceso il suo naso.',
      'Gli elfi stanno chiudendo il grande sacco.',
      'La slitta è pronta al decollo.',
    ],
    traveling: [
      'La slitta è appena passata da {city}.',
      'Babbo Natale lascia {city} col sacco un po\' più leggero.',
      'Rudolph guida la slitta dopo la tappa di {city}.',
      'A {city} hanno già ricevuto i loro regali.',
    ],
    completed: [
      'Babbo Natale ha finito il suo giro.',
      'Tutti i regali sono stati consegnati.',
      'Le renne riposano già al Polo Nord.',
      'Il prossimo Natale è già in preparazione.',
    ],
    lastVisited: 'Ultimo luogo visitato',
    nextStop: 'Prossima tappa',
    flyingTo: 'In volo verso {leg}',
    atNorthPole: 'Al Polo Nord',
    backHome: 'Di ritorno a casa',
    legLabel: {
      'north-pole': 'il Polo Nord',
      pacific: 'il Pacifico',
      oceania: "l'Oceania",
      'east-asia': "l'Asia orientale",
      'southeast-asia': "il Sud-est asiatico",
      'central-asia': "l'Asia centrale e meridionale",
      'middle-east': 'il Medio Oriente',
      'eastern-europe': "l'Europa orientale",
      africa: "l'Africa",
      'western-europe': "l'Europa occidentale",
      'south-america': "il Sud America",
      'north-america': "il Nord America",
      'north-pole-return': 'il Polo Nord',
    },
  },

  map: {
    heading: 'Il viaggio di Babbo Natale',
    subheading: 'Tocca la slitta o una città qualsiasi per saperne di più.',
    ariaLabel: 'Mappa del mondo con il viaggio di Babbo Natale',
    sleighLabel: 'Slitta di Babbo Natale',
    northPoleLabel: 'Polo Nord',
    legend: { visited: 'È già passato', current: 'È qui', upcoming: 'Non ancora' },
    stopStatus: {
      visited: 'Babbo Natale è già passato di qui',
      current: 'Babbo Natale è qui adesso',
      upcoming: 'Babbo Natale non è ancora arrivato',
    },
    localTime: 'Ora locale: {time}',
    close: 'Chiudi',
    followSleigh: 'Segui la slitta',
    wholeWorld: 'Vedi tutto il mondo',
    listHeading: 'Il viaggio, tappa per tappa',
    listIntro:
      'Lo stesso viaggio in forma di elenco, se preferisci leggerlo o se la mappa non si vede bene sul tuo schermo.',
    parentsNote:
      'Nota per mamme e papà: questa è un\'esperienza natalizia simulata. Il percorso si calcola in base ai fusi orari; non è una posizione reale né un tracciamento GPS.',
  },

  eta: {
    heading: 'A che ora arriva a casa tua?',
    body: 'Babbo Natale arriverà nella tua zona approssimativamente tra le {start} e le {end}.',
    zone: 'Calcolato per il tuo fuso orario ({zone}).',
    remaining: 'Mancano {countdown}.',
    passed: 'Babbo Natale è già passato dalla tua zona. Guarda sotto l\'albero!',
    unknownZone: 'Calcolato con l\'ora del tuo dispositivo.',
    disclaimer:
      'È una fascia indicativa, non un\'ora garantita: Babbo Natale preferisce passare quando tutti dormono.',
  },

  stats: {
    heading: {
      preparing: 'Adesso in officina',
      liftoff: 'Tutto pronto per la grande notte',
      traveling: 'Il viaggio in cifre',
      completed: 'Com\'è andato il viaggio',
    },
    labels: {
      giftsReady: 'Regali preparati',
      giftsDelivered: 'Regali consegnati',
      lettersReceived: 'Lettere ricevute',
      elvesWorking: 'Elfi al lavoro',
      reindeerTraining: 'Renne in allenamento',
      kilometers: 'Chilometri',
      citiesVisited: 'Città visitate',
      countriesVisited: 'Paesi visitati',
      homesWaiting: 'Case in attesa',
      travelMinutes: 'Tempo di volo',
    },
    units: { kilometers: 'km', hours: 'h' },
  },

  workshop: {
    heading: 'Adesso al Polo Nord',
    activities: {
      preparing: [
        'Gli elfi stanno impacchettando i regali.',
        'Babbo Natale sta leggendo le lettere.',
        'Le renne si allenano sulla pista di decollo.',
        'La signora Claus sta preparando i biscotti.',
        'La slitta sta facendo la revisione annuale.',
        'Nella sala della grande lista si controllano i nomi.',
      ],
      liftoff: [
        'La slitta sta facendo l\'ultima revisione.',
        'Rudolph si sta esercitando per la grande notte.',
        'Gli elfi caricano i sacchi sulla slitta.',
        'La signora Claus prepara la cioccolata calda per il viaggio.',
      ],
      traveling: [
        'Gli elfi seguono il viaggio dalla torre di controllo.',
        'La signora Claus lascia il camino acceso aspettando il rientro.',
        'Nell\'officina si raccolgono gli ultimi cartellini regalo.',
        'Le renne di riserva aspettano nel caso serva un cambio.',
      ],
      completed: [
        'Le renne riposano nella stalla del Polo Nord.',
        'La signora Claus serve la cioccolata del dopoviaggio.',
        'Gli elfi riordinano l\'officina per l\'anno prossimo.',
        'Babbo Natale ripone la grande lista fino al prossimo Natale.',
      ],
    },
    night: [
      'Al Polo Nord è notte: l\'officina lavora a luci basse.',
      'Gli elfi del turno di notte impacchettano in silenzio.',
      'Babbo Natale dorme un po\' prima del gran giorno.',
      'Si sente solo lo scricchiolio della neve intorno all\'officina.',
    ],
  },

  surprises: {
    northPole: 'Hai svegliato l\'aurora boreale! Gli elfi ti salutano dall\'officina.',
    celebration: 'Viaggio completato! Buon Natale.',
  },

  controls: {
    soundOn: 'Attiva l\'audio',
    soundOff: 'Silenzia',
    fullscreenEnter: 'Schermo intero',
    fullscreenExit: 'Esci da schermo intero',
    share: 'Condividi',
    shareCopied: 'Link copiato',
    motionOn: 'Attiva le animazioni',
    motionOff: 'Riduci il movimento',
  },

  share: {
    title: "Dov'è Babbo Natale?",
    text: {
      preparing: "Scopri quanto manca all'arrivo di Babbo Natale.",
      liftoff: 'Babbo Natale sta per decollare!',
      traveling: 'Guarda dove sta andando Babbo Natale!',
      completed: 'Babbo Natale ha finito il suo giro. Buon Natale!',
    },
  },

  faq: {
    heading: 'Domande frequenti',
    items: [
      {
        question: 'Quanto manca a Natale?',
        answer:
          'Te lo dice al secondo il conto alla rovescia di questa pagina. Il Natale si festeggia il 25 dicembre, e Babbo Natale parte dal Polo Nord la notte del 24.',
      },
      {
        question: "Dov'è Babbo Natale adesso?",
        answer:
          'Dipende dal giorno. Per la maggior parte dell\'anno è al Polo Nord a preparare il Natale, e la Vigilia gira tutto il mondo. La mappa di questa pagina mostra dov\'è che deve trovarsi in ogni momento.',
      },
      {
        question: 'Dove passa Babbo Natale?',
        answer:
          'Comincia dal Pacifico, prosegue per Oceania e Asia, attraversa il Medio Oriente, Europa e Africa, scende nel Sud America e termina nel Nord America prima di tornare a casa. Insegue la notte: consegna dove è già buio.',
      },
      {
        question: 'Quando parte Babbo Natale dal Polo Nord?',
        answer:
          'Decolla nel pomeriggio del 24 dicembre, quando nelle isole del Pacifico è quasi mezzanotte. Da lì non si ferma fino alla mattina del 25.',
      },
      {
        question: 'A che ora arriva Babbo Natale?',
        answer:
          'In ogni casa arriva quando i bambini sono già addormentati, più o meno tra le nove di sera e mezzanotte. Su questa pagina vedi la fascia indicativa calcolata per il tuo fuso orario.',
      },
      {
        question: 'Babbo Natale passa da tutti i paesi?',
        answer:
          'Sì: tutti i 195 paesi del mondo sono nel percorso. Sulla mappa alcune città sono segnalate come riferimento, ma la slitta passa davvero dappertutto.',
      },
      {
        question: 'Come fa a girare il mondo in una sola notte?',
        answer:
          'Perché viaggia verso ovest inseguendo la notte: andando nella stessa direzione in cui ruota la Terra, la Vigilia gli dura più di trenta ore. Questo, e un po\' di magia del Polo Nord.',
      },
      {
        question: 'Cosa succede se sono sveglio quando arriva?',
        answer:
          'Babbo Natale è molto discreto: se si accorge che qualcuno è sveglio, continua il suo cammino e torna più tardi. Il meglio per farlo passare presto è andare a letto presto.',
      },
      {
        question: 'Come sa Babbo Natale dove abito?',
        answer:
          'Dalle lettere. Ogni lettera che arriva al Polo Nord viene annotata nella grande lista col nome e la città, e gli elfi preparano il percorso con quelle informazioni.',
      },
      {
        question: 'Quando torna al Polo Nord?',
        answer:
          'La mattina del 25 dicembre, dopo l\'ultima consegna. Le renne riposano qualche giorno e l\'officina inizia a pensare al Natale successivo.',
      },
      {
        question: 'La mappa mostra la sua posizione reale?',
        answer:
          'No. È un\'esperienza natalizia simulata: il percorso si calcola in base ai fusi orari perché combaci con la notte di ogni luogo. Non c\'è nessun tracciamento GPS né posizione reale, e questa pagina non chiede mai la tua localizzazione.',
      },
      {
        question: 'Come posso parlare con Babbo Natale?',
        answer:
          'Su PapaNoel.chat puoi preparare una chiamata in cui Babbo Natale parla direttamente con tuo figlio o tua figlia, per nome e in tempo reale. Decidi tu cosa sa in anticipo.',
      },
      {
        question: 'Posso fare una videocall con Babbo Natale?',
        answer:
          'Sì. Oltre alla chiamata vocale, puoi fare una videocall personalizzata in cui Babbo Natale conosce nome, gusti, traguardi e desideri di tuo figlio o tua figlia.',
      },
    ],
  },

  cta: {
    heading: 'Mentre aspetti il suo arrivo, puoi parlare con Babbo Natale',
    body: 'Prepara una videocall personalizzata in cui Babbo Natale conoscerà il nome, i gusti, i traguardi e i desideri di tuo figlio o tua figlia.',
    primary: 'Parla con Babbo Natale',
    secondary: 'Scopri i personaggi magici',
    free: 'Volete provare prima? Parla gratis con Babbo Natale',
    travelingNote:
      'Stasera Babbo Natale è molto impegnato, ma puoi ancora preparare una videocall magica.',
  },

  stops: {
    'north-pole': 'Polo Nord',
    'north-pole-return': 'Polo Nord',
    auckland: 'Auckland',
    suva: 'Suva',
    sydney: 'Sydney',
    melbourne: 'Melbourne',
    brisbane: 'Brisbane',
    'port-moresby': 'Port Moresby',
    darwin: 'Darwin',
    tokyo: 'Tokyo',
    seoul: 'Seoul',
    shanghai: 'Shanghai',
    beijing: 'Pechino',
    'hong-kong': 'Hong Kong',
    manila: 'Manila',
    singapore: 'Singapore',
    jakarta: 'Giacarta',
    bangkok: 'Bangkok',
    dhaka: 'Dacca',
    kathmandu: 'Katmandu',
    'new-delhi': 'Nuova Delhi',
    karachi: 'Karachi',
    kabul: 'Kabul',
    dubai: 'Dubai',
    tehran: 'Teheran',
    riyadh: 'Riyadh',
    istanbul: 'Istanbul',
    moscow: 'Mosca',
    athens: 'Atene',
    warsaw: 'Varsavia',
    cairo: 'Il Cairo',
    johannesburg: 'Johannesburg',
    lagos: 'Lagos',
    berlin: 'Berlino',
    rome: 'Roma',
    paris: 'Parigi',
    madrid: 'Madrid',
    london: 'Londra',
    lisbon: 'Lisbona',
    reykjavik: 'Reykjavík',
    'rio-de-janeiro': 'Rio de Janeiro',
    'sao-paulo': 'San Paolo',
    'buenos-aires': 'Buenos Aires',
    santiago: 'Santiago del Cile',
    'la-paz': 'La Paz',
    caracas: 'Caracas',
    bogota: 'Bogotà',
    lima: 'Lima',
    'new-york': 'New York',
    toronto: 'Toronto',
    havana: "L'Avana",
    'mexico-city': 'Città del Messico',
    chicago: 'Chicago',
    denver: 'Denver',
    'los-angeles': 'Los Angeles',
    vancouver: 'Vancouver',
    anchorage: 'Anchorage',
    honolulu: 'Honolulu',
  },
}
